import { useRef, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import { useCreateBudgetBucket, useUpdateBudgetBucket, useDeleteBudgetBucket } from '../../hooks/api/budgetBuckets';
import BucketSelect from './BucketSelect';

// CALL-758: bucket selector + a single "Manage Buckets" dialog that handles add / rename / delete
// inline. Creating happens via a "New" action in the dialog's top-right corner, which appends a
// blank inline-editable row. Deleting a bucket that still has versions or configs is blocked
// server-side — the error surfaces as a toast via the delete hook.
const BucketManager = () => {
    const { t } = useTranslation('budget');
    const { buckets, selectedBucketId, setSelectedBucketId, canEdit } = useBudgetContext();

    const [showManage, setShowManage] = useState(false);
    const [renames, setRenames] = useState<Record<number, string>>({});
    // Pending new-bucket rows (not yet created). Each has a stable local key for React + edits.
    const [newDrafts, setNewDrafts] = useState<Array<{ key: number; name: string }>>([]);
    const draftKey = useRef(0);

    const { mutate: createBucket } = useCreateBudgetBucket();
    const { mutate: updateBucket } = useUpdateBudgetBucket();
    const { mutate: deleteBucket } = useDeleteBudgetBucket();

    const openManage = () => {
        setRenames(Object.fromEntries(buckets.map((b) => [b.id, b.name])));
        setNewDrafts([]);
        setShowManage(true);
    };

    const addDraft = () => setNewDrafts((prev) => [...prev, { key: draftKey.current++, name: '' }]);
    const setDraftName = (key: number, name: string) =>
        setNewDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, name } : d)));
    const removeDraft = (key: number) => setNewDrafts((prev) => prev.filter((d) => d.key !== key));

    const saveDraft = (key: number, name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        // Select the freshly created bucket and drop its draft row; the buckets list refetches.
        createBucket(
            { name: trimmed },
            {
                onSuccess: (created) => {
                    removeDraft(key);
                    setSelectedBucketId(created.id);
                },
            }
        );
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <BucketSelect buckets={buckets} value={selectedBucketId} onChange={setSelectedBucketId} />
            {canEdit && (
                <Button size="small" variant="outlined" startIcon={<SettingsIcon />} onClick={openManage}>
                    <span>{t('buckets.manage')}</span>
                </Button>
            )}

            <Dialog open={showManage} onClose={() => setShowManage(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <span>{t('buckets.manage')}</span>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addDraft}>
                        <span>{t('buckets.add')}</span>
                    </Button>
                </DialogTitle>
                <DialogContent>
                    {buckets.length === 0 && newDrafts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {t('hint.noBuckets')}
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                            {buckets.map((b) => {
                                const draft = (renames[b.id] ?? b.name).trim();
                                return (
                                    <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            value={renames[b.id] ?? b.name}
                                            onChange={(e) =>
                                                setRenames((prev) => ({ ...prev, [b.id]: e.target.value }))
                                            }
                                            fullWidth
                                        />
                                        <IconButton
                                            size="small"
                                            title={t('action.save')}
                                            aria-label={t('action.save')}
                                            disabled={!draft || draft === b.name}
                                            onClick={() => updateBucket({ id: b.id, name: draft })}
                                        >
                                            <SaveIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            title={t('action.delete')}
                                            aria-label={t('action.delete')}
                                            onClick={() => deleteBucket(b.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                );
                            })}

                            {/* CALL-758: pending new-bucket rows — inline editor + create/discard. */}
                            {newDrafts.map((d) => (
                                <Box key={`new-${d.key}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        size="small"
                                        autoFocus
                                        placeholder={t('field.bucketName')}
                                        value={d.name}
                                        onChange={(e) => setDraftName(d.key, e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') saveDraft(d.key, d.name);
                                        }}
                                        fullWidth
                                    />
                                    <IconButton
                                        size="small"
                                        title={t('action.save')}
                                        aria-label={t('action.save')}
                                        disabled={!d.name.trim()}
                                        onClick={() => saveDraft(d.key, d.name)}
                                    >
                                        <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        title={t('action.cancel')}
                                        aria-label={t('action.cancel')}
                                        onClick={() => removeDraft(d.key)}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowManage(false)} variant="outlined">
                        {t('action.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BucketManager;
